<template>
  <div v-if="!premium" class="container">
    <loading :active.sync="loading"></loading>
    <h1>Payment Info</h1>
    <p>You currently have no active subscriptions or payment information.</p>
    <p><router-link to="/profile/realtor/subscriptions">Learn more</router-link> about realtor tools available on Yarlow.</p>
  </div>
  <div v-else class="container">
    <loading :active.sync="loading"></loading>
    <div class="row">
      <div class="col">
        <h1 class="title">Payment Info</h1>
        <h5><span v-if="customFlag">Custom </span>Yarlow Realtor Subscription</h5>
        <p v-if="customFlag">You have a custom subscription with Yarlow.  Please contact Yarlow Support if you would like to review the details of your custom plan.</p>
        <p v-else>Includes branded app for clients with custom client code.</p>
      </div>
    </div>
    <div class="row" v-if="discountAmount">
      <div class="col-md-6">
        <h5>Discount Price:</h5><p> <span class="bold">${{ price - discountAmount }}</span>/month</p>
      </div>
      <div v-if="!isNaN(discountExpiration.getMonth())" class="col-md-6">
        <h5>Discount Expiration:</h5><p> {{ (discountExpiration.getMonth() + 1) + '/' + (discountExpiration.getDate() + 1) + '/' + discountExpiration.getFullYear() }}</p>
      </div>
    </div>
    <div class="row" v-else>
      <div class="col">
        <p><span class="bold">${{ price }}</span> /month</p>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <p class="payment-history-link" v-on:click="showPaymentHistory">Payment History</p>
      </div>
    </div>
    <div class="row">
      <div class="col-md-6">
        <h5>Current Subscription Period:</h5>
      </div>
      <div class="col-md-6">
        <p>{{ subscriptionPeriod }}</p>
      </div>
    </div>
    <div class="row" v-if="getSubscriptionNextBillingDate">
      <div class="col-md-6">
        <h5>Next Billing Date:</h5>
      </div>
      <div class="col-md-6">
        <p>{{ (nextBillingDate.getMonth() + 1) + '/' + (nextBillingDate.getDate() + 1) + '/' + nextBillingDate.getFullYear() }}</p>
      </div>
    </div>
    <div v-if="subscriptionPeriod != 'Pending'" class="row">
      <div class="col-2">
        <p class="no-margin-bottom">Auto-Renewal</p>
      </div>
      <div class="col-3 my-auto">
        <p class="d-inline">Off</p>
        <label class="switch" v-on:click="showConfirmModal">
          <input v-model="renewal" value="true" type="checkbox">
          <span class="slider round"></span>
        </label>
        <p class="d-inline">On</p>
      </div>
    </div>
    <div class="row">
      <div class="col-md-6">
        <h5>Payment Method:</h5>
      </div>
      <div class="col-md-6">
        <p>{{ creditCardType }} ending in {{ creditCardLastFour }}</p>
      </div>
    </div>
    <b-modal id="confirmModal" v-on:hide="modalClosed" ref="confirmModal" size="md" hide-footer>
      <div slot="modal-header" class="w-100 justify-content-center confirm-modal-header">
        <button v-on:click="hideConfirmModal" class="close d-inline" type="button" aria-label="Close">x</button>
        <h3 v-if="!renewal">Auto-Renewal Cancelation</h3>
        <h3 v-else>Enable Auto-Renewal</h3>
      </div>
      <div class="container">
        <p v-if="!renewal">Are you sure you want to cancel auto-renewal?</p>
        <p v-else>Are you sure you want to enable auto-renewal?</p>
        <div class="row">
          <div class="col">
            <button v-on:click="confirmChange" class="btn btn-primary">Confirm</button>
          </div>
        </div>
      </div>
    </b-modal>
    <payment-history-modal ref="paymentHistoryModal"></payment-history-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import PaymentHistoryModal from './PaymentHistoryModal.vue'

export default {
  components: {
    Loading,
    PaymentHistoryModal
  },
  data () {
    return {
      confirmed: false,
      loading: false
    }
  },
  computed: {
    ...mapGetters('realtor', [
      'getPremium',
      'getRealtorCode',
      'getSubscriptionPrice',
      'getSubscriptionDiscountAmount',
      'getSubscriptionDiscountExpirationDate',
      'getSubscriptionCreditCardType',
      'getSubscriptionCreditCardLastFour',
      'getSubscriptionCustomFlag',
      'getSubscriptionEndDate',
      'getSubscriptionNextBillingDate',
      'getSubscriptionRenewal',
      'getSubscriptionStartDate'
    ]),
    creditCardType () {
      return this.getSubscriptionCreditCardType
    },
    creditCardLastFour () {
      return this.getSubscriptionCreditCardLastFour
    },
    customFlag () {
      return this.getSubscriptionCustomFlag
    },
    discountAmount () {
      return this.getSubscriptionDiscountAmount
    },
    discountExpiration () {
      return new Date(this.getSubscriptionDiscountExpirationDate)
    },
    endDate () {
      return new Date(this.getSubscriptionEndDate)
    },
    nextBillingDate () {
      return new Date(this.getSubscriptionNextBillingDate)
    },
    price () {
      return this.getSubscriptionPrice
    },
    premium () {
      return this.getPremium
    },
    renewal: {
      get () {
        return this.getSubscriptionRenewal
      },
      set (value) {
        this.updateSubscriptionRenewal(value)
      }
    },
    startDate () {
      return new Date(this.getSubscriptionStartDate)
    },
    subscriptionPeriod () {
      if (this.startDate.getFullYear() > 2000) return (this.startDate.getMonth() + 1) + '/' + (this.startDate.getDate() + 1) + '/' + this.startDate.getFullYear() + ' - ' + (this.endDate.getMonth() + 1) + '/' + (this.endDate.getDate() + 1) + '/' + this.endDate.getFullYear()
      return 'Pending'
    }
  },
  methods: {
    ...mapActions('realtor', [
      'getCurrentRealtorData',
      'switchRenewalStatus'
    ]),
    ...mapMutations('realtor', [
      'updateSubscriptionRenewal'
    ]),
    async changeRenewalStatus () {
      this.loading = true
      await this.switchRenewalStatus()
      await this.getCurrentRealtorData(this.getRealtorCode)
      this.loading = false
    },
    async confirmChange () {
      this.confirmed = true
      this.hideConfirmModal()
      await this.changeRenewalStatus()
    },
    hideConfirmModal () {
      this.$refs.confirmModal.hide()
    },
    modalClosed () {
      if (!this.confirmed) this.renewal = !this.renewal
      this.confirmed = false
    },
    showConfirmModal () {
      this.$refs.confirmModal.show()
    },
    showPaymentHistory () {
      this.$refs.paymentHistoryModal.$refs.paymentHistoryModal.show()
    }
  },
  mounted () {
    this.$global.gaEvent('realtorViewPaymentInfo', 'navigation')
  }
}
</script>

<style scoped>
.bold {
  font-weight: bold;
}

.btn {
  margin-top: 0px !important;
}

input:checked + .slider {
  background-color: #FEEDAA;
}

input:checked + .slider:before {
  -webkit-transform: translateX(26px);
  -ms-transform: translateX(26px);
  transform: translateX(26px);
}

.container {
  margin-bottom: 3rem !important;
  border-radius: 25px;
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.no-margin-bottom {
  margin-bottom: 0px;
}

.payment-history-link {
  color: blue !important;
  cursor: pointer;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: .4s;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: '';
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  -webkit-transition: .4s;
  transition: .4s;
}

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.title {
  margin-bottom: 3rem;
}
</style>



// WEBPACK FOOTER //
// src/components/profile/realtorProfile/RealtorPaymentInfo.vue